import { useMemo } from 'react';
import type { Arealplan } from '@origo/arealplan';
import {
    Combobox,
    DateInput,
    Button,
    FilterPanel,
} from '@origo/origo-ui';
import {
    getFilterOptions,
    getVertikalNivaOptions,
    countActiveFilters,
    monthsAgo,
    getPlanPublishStatus,
} from './filterUtils';
import { type PlanFilters, type PlanPublishStatus, type SortOption, emptyFilters, sortOptions } from './types';
import type { PlanMetadata } from '../admin/services/PlanMetadataService';
import styles from './PlanFilterPanel.module.css';

export interface PlanFilterPanelProps {
    plans: Arealplan[];
    filters: PlanFilters;
    onFiltersChange: (f: PlanFilters) => void;
    sortOption: SortOption;
    onSortChange: (o: SortOption) => void;
    sectionCounts?: Map<string, number>;
    planMetadata?: Map<string, PlanMetadata>;
    showSectionFilter?: boolean;
    showSortControl?: boolean;
}

const SORT_OPTIONS = sortOptions.map((o) => ({
    value: `${o.field}-${o.direction}`,
    label: o.label,
}));

function formatDateValue(date: Date | null): string {
    if (!date) return '';
    return date.toISOString().split('T')[0];
}

export function PlanFilterPanel({
    plans,
    filters,
    onFiltersChange,
    sortOption,
    onSortChange,
    sectionCounts,
    planMetadata,
    showSectionFilter = false,
    showSortControl = true,
}: PlanFilterPanelProps) {
    function update(patch: Partial<PlanFilters>) {
        onFiltersChange({ ...filters, ...patch });
    }

    const statusOptions = useMemo(
        () => getFilterOptions(plans, 'planStat').map((o) => ({
            value: o.value,
            label: `${o.value} (${o.count})`,
        })),
        [plans]
    );

    const innholdsStatusOptions = useMemo(() => {
        const counts: Record<PlanPublishStatus, number> = { 'ikke-påbegynt': 0, utkast: 0, publisert: 0 };
        for (const plan of plans) {
            const meta = planMetadata?.get(plan.planId);
            counts[getPlanPublishStatus(meta)]++;
        }
        return [
            { value: 'ikke-påbegynt', label: `Ikke påbegynt (${counts['ikke-påbegynt']})` },
            { value: 'utkast', label: `Utkast (${counts.utkast})` },
            { value: 'publisert', label: `Publisert (${counts.publisert})` },
        ];
    }, [plans, planMetadata]);

    const typeOptions = useMemo(
        () => getFilterOptions(plans, 'planTypeKey').map((o) => ({
            value: o.value,
            label: `${o.value} (${o.count})`,
        })),
        [plans]
    );

    const vertNivOptions = useMemo(
        () => getVertikalNivaOptions(plans).map((o) => ({
            value: o.value,
            label: `${o.value} (${o.count})`,
        })),
        [plans]
    );

    const sectionStats = useMemo(() => {
        if (!showSectionFilter || !sectionCounts) return null;
        let withSections = 0;
        let withoutSections = 0;
        for (const plan of plans) {
            const count = sectionCounts.get(plan.planId) ?? 0;
            if (count > 0) withSections++;
            else withoutSections++;
        }
        return { withSections, withoutSections };
    }, [plans, sectionCounts, showSectionFilter]);

    const isPresetActive = (months: number) =>
        filters.dateFrom != null &&
        Math.abs(filters.dateFrom.getTime() - monthsAgo(months).getTime()) < 86400000;

    function setDatePreset(months: number | null) {
        if (months === null) {
            update({ dateFrom: null, dateTo: null });
        } else {
            update({ dateFrom: monthsAgo(months), dateTo: new Date() });
        }
    }

    return (
        <FilterPanel
            activeCount={countActiveFilters(filters)}
            onReset={() => onFiltersChange(emptyFilters)}
        >
            <Combobox
                label="Planstatus"
                multiple
                placeholder="Alle statuser"
                options={statusOptions}
                value={filters.statuses}
                onChange={(selected) => update({ statuses: selected })}
            />

            <Combobox
                label="Publiseringsstatus"
                multiple
                placeholder="Alle publiseringsstatuser"
                options={innholdsStatusOptions}
                value={filters.innholdsStatuser}
                onChange={(selected) => update({ innholdsStatuser: selected as PlanPublishStatus[] })}
            />

            <Combobox
                label="Plantype"
                multiple
                placeholder="Alle plantyper"
                options={typeOptions}
                value={filters.types}
                onChange={(selected) => update({ types: selected })}
            />

            <Combobox
                label="Vertikalnivå"
                multiple
                placeholder="Alle nivåer"
                options={vertNivOptions}
                value={filters.vertikalNivaer}
                onChange={(selected) => update({ vertikalNivaer: selected })}
            />

            {showSectionFilter && sectionStats && (
                <Combobox
                    label="Seksjoner"
                    placeholder="Alle planer"
                    value={[filters.sectionFilter]}
                    onChange={(selected) =>
                        update({ sectionFilter: (selected[0] ?? 'all') as PlanFilters['sectionFilter'] })
                    }
                    options={[
                        { value: 'all', label: 'Alle planer' },
                        { value: 'with-sections', label: `Med seksjoner (${sectionStats.withSections})` },
                        { value: 'without-sections', label: `Uten seksjoner (${sectionStats.withoutSections})` },
                    ]}
                />
            )}

            <div className={styles.dateSection}>
                <p className={styles.dateSectionLabel}>Ikrafttredelse</p>
                <div className={styles.dateRow}>
                    <DateInput
                        label="Fra"
                        value={formatDateValue(filters.dateFrom)}
                        onChange={(e) =>
                            update({ dateFrom: e.target.value ? new Date(e.target.value) : null })
                        }
                    />
                    <span className={styles.dateSeparator}>–</span>
                    <DateInput
                        label="Til"
                        value={formatDateValue(filters.dateTo)}
                        onChange={(e) =>
                            update({ dateTo: e.target.value ? new Date(e.target.value) : null })
                        }
                    />
                </div>
                <div className={styles.datePresets}>
                    <Button
                        variant={isPresetActive(6) ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setDatePreset(isPresetActive(6) ? null : 6)}
                    >
                        Siste 6 mnd
                    </Button>
                    <Button
                        variant={isPresetActive(12) ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setDatePreset(isPresetActive(12) ? null : 12)}
                    >
                        Siste år
                    </Button>
                    <Button
                        variant={isPresetActive(60) ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setDatePreset(isPresetActive(60) ? null : 60)}
                    >
                        Siste 5 år
                    </Button>
                </div>
            </div>

            {showSortControl && (
                <Combobox
                    label="Sortering"
                    placeholder="Velg sortering"
                    value={[`${sortOption.field}-${sortOption.direction}`]}
                    onChange={(selected) => {
                        const found = sortOptions.find(
                            (o) => `${o.field}-${o.direction}` === selected[0]
                        );
                        if (found) onSortChange(found);
                    }}
                    options={SORT_OPTIONS}
                />
            )}
        </FilterPanel>
    );
}
